import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrgOptional } from '../../lib/auth.js';
import { Button, Card, formatDate, useToast } from '../../components/ui.js';
import { ZestMark } from '../../components/ZestMark.js';
import { allowedProviders, idpLabel, useInvitationPreview } from './useInvitationPreview.js';
import './auth.css';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { me, refresh } = useAuth();
  const { setOrgId, orgs } = useOrgOptional();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const preview = useInvitationPreview(token || null);
  const invite = preview.data ?? null;
  // The org may restrict sign-in providers; tell the user before they hit the
  // API's 403 (system admins are never locked out).
  const wrongIdp =
    invite !== null &&
    me !== null &&
    !me.user.isSystemAdmin &&
    invite.allowedIdps !== null &&
    !invite.allowedIdps.includes(me.user.idp);
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Invitation ids whose link has been re-sent this visit, and the one in flight.
  const [resent, setResent] = useState<Record<string, true>>({});
  const [resending, setResending] = useState<string | null>(null);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<{ ok: true; orgId: string }>('/invitations/accept', {
        method: 'POST',
        body: { token },
      });
      await refresh();
      setOrgId(result.orgId);
      toast('Welcome aboard — invitation accepted.', 'success');
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(
          err.code === 'payment_required'
            ? 'This organisation has reached its plan limit. Ask its admin to upgrade before you can join.'
            : err.code === 'not_found'
              ? 'This invitation is invalid, expired, or was sent to a different email address.'
              : err.message,
        );
      } else {
        setError('Something went wrong accepting the invitation.');
      }
    } finally {
      setBusy(false);
    }
  };

  // Without the emailed one-time token the page cannot join the organisation
  // itself (the token is what proves this person controls the invited
  // mailbox) — so offer to send a fresh link to that address instead.
  const resend = async (invitationId: string) => {
    setResending(invitationId);
    setError(null);
    try {
      const result = await apiFetch<{ ok: true; email: string }>(
        `/invitations/${invitationId}/resend`,
        { method: 'POST' },
      );
      setResent((prev) => ({ ...prev, [invitationId]: true }));
      toast(`Invitation link sent to ${result.email}.`, 'success');
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'rate_limited'
          ? 'That link was sent very recently — give it a few minutes and check your inbox (and junk folder).'
          : err instanceof ApiRequestError
            ? err.message
            : 'Could not send the invitation link.',
      );
    } finally {
      setResending(null);
    }
  };

  // End this session and return to sign-in with the invitation path intact,
  // so the login screen offers only the providers this organisation allows.
  const switchAccount = async () => {
    setSwitching(true);
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
      queryClient.clear();
      navigate('/login', {
        replace: true,
        state: { from: location.pathname + location.search },
      });
    } catch {
      setSwitching(false);
      toast('Could not sign out. Please try again.', 'error');
    }
  };

  const pending = me?.pendingInvitations ?? [];

  return (
    <div className="auth-viewport">
      <Card className="auth-card auth-card-wide">
        <div className="auth-brand">
          <ZestMark className="auth-logo" />
          <h1>
            {token
              ? invite
                ? `Join ${invite.orgName}`
                : 'Accept invitation'
              : 'Join your organisation'}
          </h1>
        </div>
        <p className="auth-tagline">
          You are signed in as <strong>{me?.user.email}</strong>
          {invite ? ` — invited as ${invite.role}.` : '.'}
        </p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        {token && wrongIdp ? (
          <>
            <p className="auth-note">
              {invite.orgName} requires sign-in with{' '}
              {allowedProviders(invite.allowedIdps).map(idpLabel).join(' or ')}. You are signed
              in with {idpLabel(me.user.idp)}.
            </p>
            <div className="auth-actions">
              <Button loading={switching} onClick={() => void switchAccount()}>
                Sign out and switch account
              </Button>
              {orgs.length > 0 && <Link to="/">Not now</Link>}
            </div>
          </>
        ) : token ? (
          <>
            {pending.length > 0 && (
              <>
                <p className="auth-note">Invitations waiting for this email address:</p>
                <ul className="auth-invite-list">
                  {pending.map((inv) => (
                    <li key={inv.id} className="auth-invite-item">
                      <div>
                        <div className="auth-invite-org">{inv.orgName}</div>
                        <div className="auth-invite-meta">
                          Role: {inv.role} · expires {formatDate(inv.expiresAt)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="auth-actions">
              <Button loading={busy} onClick={() => void accept()}>
                Accept invitation
              </Button>
              {orgs.length > 0 && <Link to="/">Not now</Link>}
            </div>
          </>
        ) : pending.length > 0 ? (
          <>
            <p className="auth-note">
              You have been invited to join the organisation(s) below. To finish joining, open
              the <strong>Accept the invitation</strong> link in the email that was sent to you —
              or have a fresh link sent now.
            </p>
            <ul className="auth-invite-list">
              {pending.map((inv) => (
                <li key={inv.id} className="auth-invite-item">
                  <div>
                    <div className="auth-invite-org">{inv.orgName}</div>
                    <div className="auth-invite-meta">
                      Role: {inv.role} · expires {formatDate(inv.expiresAt)}
                    </div>
                  </div>
                  {resent[inv.id] ? (
                    <span className="auth-invite-meta">Link sent — check your inbox</span>
                  ) : (
                    <Button
                      variant="secondary"
                      loading={resending === inv.id}
                      disabled={resending !== null}
                      onClick={() => void resend(inv.id)}
                    >
                      Email me the link
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="auth-note">
            This page needs an invitation link. Check the invitation you received for the full
            URL, or ask the organisation admin to send it again.
          </p>
        )}

        <p className="auth-footer-note">
          The invitation must have been sent to the email address you are signed in with.
          {!token && (
            <>
              {' '}
              Not joining an existing team?{' '}
              <Link to="/onboarding/create-org">Create a new organisation</Link>.
            </>
          )}
        </p>
      </Card>
    </div>
  );
}
